/// <amd-module name="DistributedTask/Subscriptions/Components/SubscriptionPicker" />
import { ISubscriptionPickerProps } from "Components/SubscriptionPicker.Props";
import { Subscription } from "Models/Subscription";
import * as React from "react";
import * as SubscriptionResources from "DistributedTask/Subscriptions/Resources";
import { Button } from "VSSUI/Button";
import { IMessageBarProps, MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { localeFormat } from "VSS/Core/Util/String";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface ISubscriptionPickerInternalState {
    currentlySubscriptionId?: string;
    selectedSubscriptionIsValid?: boolean;
    selectedSubscroptionIsValidPromise?: Promise<boolean>;
}

export class SubscriptionPicker extends React.Component<ISubscriptionPickerProps, ISubscriptionPickerInternalState>  {

    private get selectedSubscriptionId(): string {
        const { selectedSubscriptionId, defaultOrganizationLinkedSubscription } = this.props;
        const { organizationSubscription } = this.props.subscriptionContext.state;
        if (selectedSubscriptionId) {
            return selectedSubscriptionId;
        } else if (defaultOrganizationLinkedSubscription && organizationSubscription) {
            return organizationSubscription.id;
        } else {
            // do not return undefined so that radio group selects non-first element
            return "noneselected";
        }
    }

    public static getDerivedStateFromProps(
        nextProps: Readonly<ISubscriptionPickerProps>,
        prevState: Readonly<ISubscriptionPickerInternalState>): Partial<ISubscriptionPickerInternalState> | null {
        const { selectedSubscriptionId, validateSubscriptionChoices } = nextProps;
        const { currentlySubscriptionId } = prevState;
        if (selectedSubscriptionId !== currentlySubscriptionId) {
            const validateSubscriptionPromise = validateSubscriptionChoices
                ? SubscriptionPicker.getValidateSubscriptionPromise(nextProps)
                : undefined;
            return {
                currentlySubscriptionId: selectedSubscriptionId,
                selectedSubscroptionIsValidPromise: validateSubscriptionPromise,
                selectedSubscriptionIsValid: undefined,
            };
        }

        return null;
    }

    private static subscriptionIdToIsValidPromise: { [subscriptionId: string]: Promise<boolean> } = {};

    private static getValidateSubscriptionPromise(props: ISubscriptionPickerProps): Promise<boolean> {
        const { organizationSubscription } = props.subscriptionContext.state;
        const { hostId, selectedSubscriptionId } = props;
        const { validateSubscription } = props.subscriptionContext.actions;
        if (!selectedSubscriptionId || (organizationSubscription && selectedSubscriptionId === organizationSubscription.id)) {
            return Promise.resolve(true);
        } else {
            const validatePromise = this.subscriptionIdToIsValidPromise[selectedSubscriptionId] || validateSubscription(selectedSubscriptionId, hostId);
            this.subscriptionIdToIsValidPromise[selectedSubscriptionId] = validatePromise;
            return validatePromise;
        }
    }
    private mounted: boolean = false;

    constructor(props: ISubscriptionPickerProps, context?: any) {
        super(props, context);
        this.state = {};
    }

    public async componentDidUpdate() {
        if (this.props.useDropdown && this.props.selectedSubscriptionId) {
            const selectionValue = this._subscriptionDropdownSelection.value[0];
            const beginIndex = selectionValue && selectionValue.beginIndex;
            const subscriptions = this.props.subscriptionContext.state.subscriptions || [];
            const selectedSubIndex = subscriptions.map((sub: Subscription) => sub.id).indexOf(this.props.selectedSubscriptionId);
            if (selectedSubIndex !== beginIndex && selectedSubIndex >= 0) {
                this._subscriptionDropdownSelection.select(selectedSubIndex);
            }
        }

        const {
            selectedSubscroptionIsValidPromise: validatePromise,
            currentlySubscriptionId,
        } = this.state;
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;
        if (currentlySubscriptionId && validatePromise) {
            // clear so we don't check many times
            this.setState({
                selectedSubscroptionIsValidPromise: undefined,
            });
            const isValid = await validatePromise;
            if (this.props.onSubscriptionValidated) {
                this.props.onSubscriptionValidated(currentlySubscriptionId, isValid);
            }
            if (this.mounted) {
                this.setState((prevState) => {
                    if (prevState.currentlySubscriptionId === this.state.currentlySubscriptionId) {
                        return {
                            selectedSubscriptionIsValid: isValid,
                        };
                    }
                    return null;
                });
            }
        }
    }

    public componentDidMount() {
        this.mounted = true;
        this.componentDidUpdate();
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public render(): JSX.Element | null {
        const { className } = this.props;
        return (
            <div className={css("flex-column flex-noshrink", className)}>
                {this.renderSubscriptionChoiceGroup()}
                {this.renderCreateSubscriptionButton()}
            </div >);
    }

    private renderSubscriptionChoiceGroup() {
        const { loadedAllSubscriptions, subscriptions } = this.props.subscriptionContext.state;
        const { label, titleClassName } = this.props;
        if (!loadedAllSubscriptions || !subscriptions) {
            return (
                <div className="subscription-picker-spinner">
                    <Spinner ariaLive="assertive" label={SubscriptionResources.LoadingSubscriptions} size={SpinnerSize.medium} />
                </div>);
        } else if (subscriptions.length === 0) {
            return <div className={css(this.props.useDropdown ? "subscription-picker-dropdown-title bolt-formitem-label body-m" : "bolt-formitem-label body-m", titleClassName)}>{SubscriptionResources.NoAzureSubscriptionsMessage}</div>;
        } else {
            const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;
            const selectedSubscription = subscriptionIdToSubscription[this.state.currentlySubscriptionId];

            return <React.Fragment>
                <div
                    className={css(this.props.useDropdown ? "subscription-picker-dropdown-title" : "", titleClassName)}>
                    {label || SubscriptionResources.SubscriptionPickerTitle}
                </div>
                {this.props.useDropdown ?
                    (<div className={"subscription-picker-dropdown-holder"}>
                        <Dropdown
                            ariaLabel={label || SubscriptionResources.SubscriptionPickerTitle}
                            className={css("subscription-picker-dropdown", this.props.dropdownClassName)}
                            calloutContentClassName={css("subscription-picker-dropdown-callout", this.props.dropdownCalloutClassName)}
                            actions={[{
                                text: SubscriptionResources.NewAzureSubscription,
                                ariaLabel: SubscriptionResources.NewAzureSubscription,
                                iconProps: { iconName: "Add" },
                                onClick: this.onCreateSubscriptionButtonClick
                            }]}
                            selection={this._subscriptionDropdownSelection}
                            items={subscriptions.filter((sub: Subscription) => { return sub.isUsable; })
                                .map((sub: Subscription) => {
                                    const name = localeFormat("{0} ({1})", sub.displayName, sub.id);
                                    return sub && { id: sub.id, text: name, tooltipText: name, data: sub } as IListBoxItem<Subscription>;
                                })}
                            onSelect={(event: any, selectedItem: IListBoxItem<Subscription>) => {
                                this.props.onSubscriptionChoiceChanged(selectedItem.data);
                            }}
                            filterThrottleWait={200}
                        />
                    </div >)
                    : (
                        <div className="flex-column flex-noshrink">
                            <div className="subscription-picker-choiceGroupContainer">
                                <RadioButtonGroup
                                    selectedButtonId={this.selectedSubscriptionId}
                                    onSelect={this.onChoiceGroupChange}
                                >
                                    {
                                        subscriptions.map((subscription: Subscription) => {
                                            const { id, isUsable, displayName } = subscription;
                                            const radioButtonAriaLabelId = `subscription-picker-${id}`;
                                            return <RadioButton
                                                className="subscription-picker-radiobutton body-m"
                                                id={id}
                                                key={`radioButton-${id}-key`}
                                                ariaLabelledBy={radioButtonAriaLabelId}
                                                disabled={!isUsable || this.props.disabled}
                                            >
                                                <div className="bolt-radio-button-label subscription-picker-radiobutton-section" id={radioButtonAriaLabelId}>
                                                    {displayName}
                                                    <span className="subscription-picker-radiobuttonsublabel body-s">{id}</span>
                                                </div>
                                            </RadioButton>;
                                        })
                                    }
                                </RadioButtonGroup >
                            </div >
                            {this.renderValidationMessageBar()}
                        </div >
                    )}
            </React.Fragment>;
        }
    }

    private renderValidationMessageBar(): JSX.Element | null {
        const { validateSubscriptionChoices } = this.props;
        const { selectedSubscriptionIsValid } = this.state;
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;

        if (!validateSubscriptionChoices) {
            return null;
        }

        const subscription = this.selectedSubscriptionId && subscriptionIdToSubscription
            ? subscriptionIdToSubscription[this.selectedSubscriptionId]
            : undefined;

        // calculate message to show based on loading, waiting for input, valid and invalid
        const messageBarProps: IMessageBarProps = {};
        let showSpinner: boolean = false;
        let messageBarMessage: string = "";
        if (subscription) {
            if (selectedSubscriptionIsValid === undefined) {
                // validating subscription
                showSpinner = true;
                messageBarProps.severity = MessageBarSeverity.Info;
                messageBarMessage = SubscriptionResources.ValidatingSubscription;
            } else if (selectedSubscriptionIsValid) {
                // subscription is valid
                messageBarProps.severity = MessageBarSeverity.Success;
                messageBarMessage = SubscriptionResources.SubscriptionValid;
            } else {
                // subscription is invalid
                messageBarProps.severity = MessageBarSeverity.Error;
                messageBarMessage = SubscriptionResources.SubscriptionInvalid + ". " + subscription.invalidReason;
            }
        } else {
            // waiting for selection
            messageBarProps.severity = MessageBarSeverity.Info;
            messageBarMessage = SubscriptionResources.WaitingToValidate;
        }

        return (
            <div className="subscription-picker-messageBarContainer">
                <MessageBar {...messageBarProps}>
                    {this.renderMessageBarContent(messageBarMessage, showSpinner)}
                </MessageBar>
            </div>);
    }

    private renderMessageBarContent(message: string, spinner: boolean): JSX.Element {
        return (
            <div className="subscription-picker-messageBarContent flex-row flex-noshrink">
                {spinner && <Spinner ariaLive="assertive" label={message} size={SpinnerSize.small} />}
            </div>);
    }

    private renderCreateSubscriptionButton() {
        if (!this.props.showCreateButtonForNoSubscriptions && !this.props.showCreateSubscriptionButton) {
            return undefined;
        }

        const { loadedAllSubscriptions, subscriptions } = this.props.subscriptionContext.state;
        if (this.props.showCreateButtonForNoSubscriptions && !(loadedAllSubscriptions && subscriptions && subscriptions.length)) {

            // showCreateButtonForNoSubscriptions takes precedence over showCreateSubscriptionButton
            return undefined;
        }

        return (
            <div className="subscription-picker-create-button-container">
                <Button
                    primary={true}
                    ariaLabel={SubscriptionResources.NewAzureSubscriptionAriaLabel}
                    text={SubscriptionResources.NewAzureSubscription}
                    onClick={this.onCreateSubscriptionButtonClick}
                    iconProps={{ iconName: "Add" }}
                />
            </div>);
    }

    private onCreateSubscriptionButtonClick = () => {
        const { createSubscriptionReplyTo, createSubscriptionReplyToQueryParams } = this.props;
        const { redirectToCreateSubscription } = this.props.subscriptionContext.actions;
        redirectToCreateSubscription(createSubscriptionReplyTo, createSubscriptionReplyToQueryParams);
    }

    private onChoiceGroupChange = async (subscriptionId: string): Promise<void> => {
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;
        this.props.onSubscriptionChoiceChanged(subscriptionIdToSubscription[subscriptionId]);
    }

    private _subscriptionDropdownSelection = new DropdownSelection();
}
